<template>
    <div class="content-wrapper">
        <div class="d-flex align-items-center justify-content-between">
            <app-breadcrumb :page-title="$t('invoice_details')"/>
            <div class="mb-4">
                <button type="button" v-if="$can('update_invoices')"
                        class="btn btn-warning btn-with-shadow mr-1"
                        @click="editInvoice">
                    <app-icon name="edit" class="size-20 mr-2"/>
                    {{ $t('edit') }}
                </button>
                <button type="button" v-if="$can('invoice_download')"
                        class="btn btn-primary btn-with-shadow mr-1"
                        @click="downloadInvoice">
                    <app-icon name="download" class="size-20 mr-2"/>
                    {{ $t('download') }}
                </button>
                <button type="button" v-if="$can('invoice_send')"
                        class="btn btn-success btn-with-shadow"
                        @click="openSendInvoiceModal">
                    <app-icon name="send" class="size-20 mr-2"/>
                    {{ $t('send') }}
                </button>
            </div>
        </div>

        <div class="row justify-content-center">
            <div class="col-xl-9" v-if="dataLoaded">
                <div id="print-invoice">
                    <div class="primary-card-color rounded invoice-wrapper">
                        <div class="p-primary border-bottom w-100">
                            <div class="d-flex align-items-center justify-content-between">
                                <img :src="AppFunction.getAppUrl(configData.invoice_logo)" class="width-100 height-70"
                                     alt="">
                                <div class="width-150">
                                    <p class="mb-1">{{ $t('invoice_no') }}: {{ formData.invoice_number }}</p>
                                    <p class="mb-0 font-size-90 text-muted">{{ $t('date') }}:
                                        {{ formatDateToLocal(formData.date) }}</p>
                                    <p class="mb-0 font-size-90 text-muted">{{ $t('due_date') }}:
                                        {{ formatDateToLocal(formData.due_date) }}</p>
                                </div>
                            </div>
                        </div>
                        <div class="p-primary border-bottom w-100">
                            <div class="row">
                                <div class="col-sm-6">
                                    <h5 class="text-muted">{{ $t('to') }}</h5>
                                    <p class="mb-1 font-weight-bold">{{ formData.client.full_name }}</p>
                                    <template v-if="formData.client.profile">
                                        <p v-if="formData.client.profile.contact" class="mb-0 font-size-90">
                                            {{ $t('contact') }}: {{ formData.client.profile.contact }}
                                        </p>
                                        <p v-if="formData.client.profile.address" class="mb-0 font-size-90">
                                            {{ $t('address') }}: {{ formData.client.profile.address }}
                                        </p>
                                    </template>
                                </div>
                                <div class="col-sm-6">
                                    <h5 class="text-muted">{{ $t('from') }}</h5>
                                    <p class="mb-1 font-weight-bold" v-if="formData.created_by">
                                        {{ formData.created_by.full_name }}</p>
                                    <template v-if="formData.created_by.profile">
                                        <p v-if="formData.created_by.profile.contact" class="mb-0 font-size-90">
                                            {{ $t('contact') }}: {{ formData.created_by.profile.contact }}
                                        </p>
                                        <p v-if="formData.created_by.profile.address" class="mb-0 font-size-90">
                                            {{ $t('address') }}: {{ formData.created_by.profile.address }}
                                        </p>
                                    </template>
                                </div>
                            </div>
                        </div>
                        <div class="datatable p-primary">
                            <div class="min-height-100 table-responsive">
                                <table class="table mb-primary">
                                    <thead class="thead-dark">
                                    <tr>
                                        <th class="border-0 p-3">{{ $t('product') }}</th>
                                        <th class="border-0 text-right p-3">{{ $t('quantity') }}</th>
                                        <th class="border-0 text-right p-3">{{ $t('unit_price') }}</th>
                                        <th class="border-0 text-right p-3">{{ $t('tax') }}</th>
                                        <th class="border-0 text-right p-3">{{ $t('total') }}</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr class="default-base-color border-bottom"
                                        v-for="invoiceProduct in formData.invoice_details">
                                        <td class="border-0 px-3">
                                            {{ invoiceProduct.product.name }}
                                        </td>
                                        <td class="border-0 text-right px-3">
                                            {{ invoiceProduct.quantity }}
                                        </td>
                                        <td class="border-0 text-right px-3">
                                            <p class="text-nowrap">
                                                {{ numberWithCurrencySymbol(invoiceProduct.price) }}
                                            </p>
                                        </td>
                                        <td class="border-0 text-right px-3">
                                            <p class="text-nowrap">
                                                {{
                                                    invoiceProduct.tax ?
                                                        numberWithCurrencySymbol(calculateProductTax
                                                        (invoiceProduct.tax.value, (invoiceProduct.quantity * invoiceProduct.price))) :
                                                        'N/A'
                                                }}
                                            </p>
                                        </td>
                                        <td class="border-0 text-right px-3">
                                            <p class="text-nowrap">
                                                {{

                                                    numberWithCurrencySymbol(
                                                        calculateProductTax(
                                                            invoiceProduct.tax ? invoiceProduct.tax.value : 0, invoiceProduct.quantity * invoiceProduct.price
                                                        ) + (invoiceProduct.quantity * invoiceProduct.price))
                                                }}
                                            </p>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="row justify-content-end">
                                <div class="col-8 col-sm-6 col-xl-5">
                                    <div class="text-size-16 border-bottom mb-2">
                                        <div class="d-flex align-items-center justify-content-between py-2">
                                            <p class="text-muted mb-0">Sub total</p>
                                            <p class="mb-0">{{ numberWithCurrencySymbol(formData.sub_total) }}</p>
                                        </div>
                                        <div class="d-flex align-items-center justify-content-between py-2">
                                            <p class="text-muted mb-0">Tax</p>
                                            <p class="mb-0">{{ numberWithCurrencySymbol(totalTax) }}</p>
                                        </div>
                                        <div class="d-flex align-items-center justify-content-between py-2">
                                            <p class="text-muted mb-0">Discount</p>
                                            <p v-if="formData.discount_type === 'percentage'" class="mb-0">
                                                {{ formData.discount }} %</p>
                                            <p v-else class="mb-0">{{ numberWithCurrencySymbol(formData.discount) }}</p>
                                        </div>
                                        <div class="d-flex align-items-center justify-content-between py-2">
                                            <p class="text-muted mb-0">Total</p>
                                            <p class="mb-0">{{ numberWithCurrencySymbol(formData.total) }}</p>
                                        </div>
                                        <div class="d-flex align-items-center justify-content-between py-2">
                                            <p class="text-muted mb-0">Paid</p>
                                            <p class="mb-0">{{ numberWithCurrencySymbol(formData.received_amount) }}</p>
                                        </div>
                                    </div>
                                    <div class="d-flex align-items-center justify-content-between">
                                        <h4 class="text-muted mb-0">Due amount</h4>
                                        <h4 class="mb-0">{{ numberWithCurrencySymbol(formData.due_amount) }}</h4>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="p-primary">
                            <div class="mb-2" v-if="formData.notes">
                                <p class="mb-0 font-weight-bold">{{ $t('notes') }}</p>
                                <span v-html="formData.notes"></span>
                            </div>
                            <div class="mb-2" v-if="formData.terms">
                                <p class="mb-0 font-weight-bold">{{ $t('terms') }}</p>
                                <span v-html="formData.terms"></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <app-overlay-loader v-else/>
        </div>

        <send-invoice-modal
            v-if="isSendInvoiceModalActive"
            :email="formData.client.email"
            :invoice-information="formData"
            @closeModal="closeSendInvoiceModal"
        />

        <!-- Client Add/Edit Modal -->
        <invoice-add-edit-modal
            v-if="isInvoiceAddEditModalActive"
            :table-id="tableId"
            :selected-url="selectUrl"
            :productList="productList"
            :status-list="statusList"
            :tax-list="taxList"
            :recurring-cycle-list="recurringCycleList"
            :clientList="clientList"
            :currencyList="currencyList"
            @success="afterUpdate"
            @closeModal="closeInvoiceModal"
        />
    </div>
</template>

<script>
import AppFunction from '../../../../core/helpers/app/AppFunction';
import {SubmitFormMixins} from "../../../Mixins/billar/SubmitFormMixins";
import {numberWithCurrencySymbol, formatDateToLocal} from "../../../Helpers/Helpers";
import {mapGetters} from "vuex";
import {INVOICES} from "../../../Config/BillarApiUrl";

export default {
    name: 'InvoiceDetails',
    mixins: [SubmitFormMixins],
    props: {
        configData: {}
    },
    data() {
        return {
            numberWithCurrencySymbol,
            formatDateToLocal,
            dataLoaded: false,
            AppFunction,
            isSendInvoiceModalActive: false,
            isInvoiceAddEditModalActive: false,
            productList: [],
            selectUrl: '',
            formData: {},
        }
    },
    computed: {
        totalTax() {
            let totalTaxes = 0;
            this.formData.invoice_details.map((item) => {
                if (item.tax) {
                    totalTaxes = totalTaxes + (((item.price * item.quantity) * item.tax.value) / 100)
                } else {
                    totalTaxes = totalTaxes + 0;
                }
            });
            return totalTaxes;
        },
        ...mapGetters({
            statusList: "getInvoiceStatus",
            recurringCycleList: "getRecurringCycle",
            taxList: "getTax",
            clientList: "getClientUser",
            currencyList: "getCurrency",
        }),
    },
    methods: {
        calculateProductTax(tax, totalPrice) {
            let taxes = 0;
            taxes = ((totalPrice * tax) / 100);
            return taxes;
        },
        afterSuccessFromGetEditData(response) {
            this.formData = response.data;
            this.dataLoaded = true;
        },
        editInvoice() {
            this.selectUrl = `${INVOICES}/${this.formData.id}`;
            this.isInvoiceAddEditModalActive = true;
        },
        downloadInvoice() {
            window.open(AppFunction.getAppUrl(`invoice-download/${this.formData.id}`))
        },
        openSendInvoiceModal() {
            this.isSendInvoiceModalActive = true;
        },
        closeSendInvoiceModal() {
            this.isSendInvoiceModalActive = false;
            $('#send-invoice-modal').modal('hide');
        },
        getAllProduct() {
            this.axiosGet(`products?all`).then(({data}) => {
                this.productList = data
            })
        },
        closeInvoiceModal() {
            this.selectUrl = "";
            this.isInvoiceAddEditModalActive = false;
        },
        afterUpdate() {
            window.location.reload();
        }
    },
    mounted() {
        this.getAllProduct();
        this.$store.dispatch("getInvoiceStatus");
        this.$store.dispatch("getRecurringCycle");
        this.$store.dispatch("getTax");
        this.$store.dispatch("getClientUser");
        this.$store.dispatch("getCurrency");
    }
}
</script>